import { debounce } from './utils';
import { withCartSource } from '@/lib/cart-source';
import type { SwymWishlistProduct } from '@/@types/global';

export type BoostParsedVariant = {
  title: string;
  price: string;
  inventoryQuantity: string;
  inventoryPolicy: string;
  id: string;
  available: boolean;
  preorder: string;
};

export type BoostProductData = {
  handle: string;
  variantId: string;
  splitProduct: string;
  variants: string;
  priceMin: string;
  priceMax: string;
  compareAtPriceMin: string;
  compareAtPriceMax: string;
  images: Array<{
    src: string;
    width: number;
    alt: string;
    id: number;
    position: number;
    height: number;
  }>;
  options_with_values: Array<{
    original_name: string;
    values: Array<{
      image: any;
      title: string;
    }>;
    name: string;
    label: string;
    position: number;
  }>;
  selectedVariantImageByFilterOption: string;
  category: string;
  tags: Array<string>;
};

async function addToCart(variantId: string, qty: number = 1) {
  let formData = {
    items: [
      {
        id: +variantId,
        quantity: qty,
        properties: withCartSource(null, 'collection-page-updates:addToCart'),
      },
    ],
  };

  const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    const reason = await response.text();
    throw new Error('Failed to add to cart: ' + reason);
  }

  return response.json();
}

function onBoostATCClick(e: Event) {
  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation();

  const cardWrapper = (e.target as HTMLElement).closest('.boost-sd__product-item');

  if (!cardWrapper) return;

  const productData = cardWrapper.getAttribute('data-product');

  if (!productData) return;

  // Stops keyboard popping up
  const focusedElement = document.activeElement as HTMLElement;
  focusedElement.blur();

  const product: BoostProductData = JSON.parse(productData),
    variants = JSON.parse(product.variants) as BoostParsedVariant[],
    hasMultipleVariants = variants.length > 1;

  const productID = cardWrapper.getAttribute('data-product-id') || 0;

  if (!productID) return;

  if (hasMultipleVariants) {
    window.selectOptionsHandler.handleRequest(productID);
    return;
  } else {
    // Just add to cart here instead of letting Boost do anything.
    // Boost either tries to open the Ajax cart, or redirects users to the cart page

    const variantId = product.variantId,
      variant = variants.find((v) => v.id === variantId);

    if (!variantId || !variant) return;

    const priceCents = parseFloat(variant.price) * 100,
      baseCurrencyPriceCents =
        window.Currency?.convert(
          priceCents,
          window.Shopify.currency.active,
          window.theme.shopCurrency,
        ) ?? 0;

    window.PurchaseGiftManager.showModal({
      currentVariantId: variantId,
      variant: {
        inventory_quantity: parseFloat(variant.inventoryQuantity),
        price: baseCurrencyPriceCents,
      },
      qty: 1,
    }).then(async function ({ status }) {
      if (status === 'user-rejected') {
        return addToCart(variantId);
      }

      if (status === 'not-eligible') {
        return addToCart(variantId);
      }

      if (status === 'not-needed') return addToCart(variantId);

      if (status === 'add-multiple') {
        return addToCart(variantId, window.theme.gift_product.required_amount);
      }
    });
  }
}

let boostAtcOverrideAbortController: AbortController | null = null;
let boostAtcOverrideInitialized = false;
let lastBoostAtcHandledAt = 0;

function OverrideBoostATC() {
  if (boostAtcOverrideInitialized) return;
  boostAtcOverrideInitialized = true;

  boostAtcOverrideAbortController?.abort();
  boostAtcOverrideAbortController = new AbortController();
  const controller = boostAtcOverrideAbortController;

  const delegatedBoostAtcHandler = (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const btn = target.closest('.boost-sd__product-info .boost-sd__button');
    if (!btn) return;

    // A single interaction can emit multiple events (eg. pointerdown -> click).
    // Only allow the ATC logic to run once per interaction, but still block Boost's handlers.
    const now = Date.now();
    // if (e.type === 'pointerdown') {
    //   e.preventDefault();
    //   e.stopPropagation();
    //   e.stopImmediatePropagation();

    //   lastBoostAtcHandledAt = now;
    //   onBoostATCClick(e);
    //   return;
    // }

    if (e.type === 'click') {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (now - lastBoostAtcHandledAt < 1000) {
        return;
      }

      lastBoostAtcHandledAt = now;
      onBoostATCClick(e);
      return;
    }
  };

  (['pointerdown', 'click'] as const).forEach((eventName) => {
    document.addEventListener(eventName, delegatedBoostAtcHandler, {
      capture: true,
      signal: controller.signal,
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  const updateBadges = () => {
    window.isUpdatingBadges = true;

    setTimeout(() => {
      document.querySelectorAll<HTMLElement>('.boost-sd__product-item').forEach((productItem) => {
        const id = productItem.getAttribute('data-product-id');
        const url = productItem.querySelector<HTMLAnchorElement>('.boost-sd__product-link')?.getAttribute('href');
        const title = productItem.querySelector('.boost-sd__product-title')?.textContent?.trim() ?? '';
        productItem.querySelectorAll('.boost-product-review').forEach((review) => {
          review.replaceWith(
            `<span class="stamped-product-reviews-badge stamped-main-badge" data-id="${id}" data-product-title="${title}" style="display: block;"></span>`,
          );
        });
        // Add Wishlist button
        if (!productItem.querySelector('.wishlist_btn'))
          productItem.insertAdjacentHTML(
            'beforeend',
            `<button class="wishlist_btn swym-button swym-add-to-wishlist-view-product product_${id} swym-icon swym-heart swym-loaded" data-swaction="addToWishlist" data-depends-on="swym" data-product-id="${id}" data-variant-id="${id}" data-product-url="${url}" aria-label="Add to Wishlist"><span class="swym-tooltip swym-inject swym-tooltip-clickable"><span class="swym-tooltip-text"></span></span></button>`,
          );
      });

      if (window.StampedFn) window.StampedFn.loadBadges();

      function swymCallbackFn() {
        _swat.fetchWrtEventTypeET(fetchCallbackFn, 4);
      }
      function fetchCallbackFn(products: SwymWishlistProduct[]) {
        document.querySelectorAll<HTMLElement>('.boost-sd__product-item').forEach((productItem) => {
          const activeProductId = productItem.getAttribute('data-product-id');

          // `empi` is numeric, the data attribute is a string; compare as
          // strings to keep the coercion the original loose `==` relied on.
          const isInWishlist = products.find(function (p) {
            return String(p.empi) === activeProductId;
          });
          const wishlistButton = productItem.querySelector<HTMLButtonElement>('.wishlist_btn');
          if (!wishlistButton) return;

          if (isInWishlist) {
            wishlistButton.disabled = true;
            wishlistButton.classList.add('swym-added');
          } else {
            wishlistButton.addEventListener('click', function (e) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              const url = productItem.querySelector<HTMLAnchorElement>('.boost-sd__product-link')?.getAttribute('href');

              const fetchDetails = async () => {
                const res = await fetch(`${url}.json`);
                const data = await res.json();
                var activeProductId = data.product.id;
                var activeVariantId = data.product.variants[0].id;
                if (SwymPageData) {
                  SwymPageData.empi = activeProductId;
                  SwymPageData.epi = activeVariantId;
                  SwymPageData.et = 4; // et 4 stands for wishlist event type
                  _swat.addToWishList(SwymPageData, function (e) {
                    wishlistButton.classList.add('swym-added');
                  });
                }
              };
              fetchDetails();
            });
          }
        });
      }

      if (!window.SwymCallbacks) {
        window.SwymCallbacks = [];
      }

      window.SwymCallbacks.push(swymCallbackFn);
      window.isUpdatingBadges = false;
    }, 250);
  };
  window.defer(() => {
    updateBadges();
    window.isUpdatingBadges = false;

    const targetNode = document.querySelector('.boost-sd__filter-product-list');
    const config = { attributes: true, childList: true, subtree: true };
    const callback = debounce((mutationList, observer) => {
      // if (!window.isUpdatingBadges) updateBadges();
      // No-op: ATC override uses document-level delegation so it applies to dynamically added products.
    }, 1000);
    const observer = new MutationObserver(callback);
    if (targetNode) observer.observe(targetNode, config);
    OverrideBoostATC();
  }, '.boost-sd__product-rating');
});
